/**
 * Spa Treatments Menu Data
 * Phase D2.5: Luxury Spa Booking voice demo
 */

export interface SpaTreatment {
  id: string;
  name: string;
  description: string;
  price: number;
  durationMinutes: number;
  aliases: string[]; // Voice recognition aliases
}

export const spaTreatments: SpaTreatment[] = [
  {
    id: 'swedish-massage',
    name: 'Swedish Massage',
    description: 'A classic relaxing full-body massage using long, flowing strokes to ease tension and promote wellness.',
    price: 120,
    durationMinutes: 60,
    aliases: ['swedish massage', 'swedish', 'relaxing massage', 'classic massage'],
  },
  {
    id: 'deep-tissue',
    name: 'Deep Tissue Massage',
    description: 'Intensive massage targeting deep muscle layers to release chronic tension and knots.',
    price: 160,
    durationMinutes: 90,
    aliases: ['deep tissue massage', 'deep tissue', 'deep massage', 'intensive massage', 'sports massage'],
  },
  {
    id: 'facial-rejuvenation',
    name: 'Facial Rejuvenation',
    description: 'Luxurious facial treatment using premium organic products to restore your natural glow.',
    price: 140,
    durationMinutes: 75,
    aliases: ['facial rejuvenation', 'facial', 'face treatment', 'rejuvenation', 'skin treatment'],
  },
];

// Spa images for slideshow
export const SPA_IMAGES = [
  'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=800&h=600&fit=crop',
];

/**
 * Find a spa treatment by voice input (case-insensitive alias matching)
 */
export function findTreatmentByVoice(input: string): SpaTreatment | null {
  const normalizedInput = input.toLowerCase().trim();

  // First try exact alias match
  for (const treatment of spaTreatments) {
    if (treatment.aliases.some((alias) => normalizedInput.includes(alias))) {
      return treatment;
    }
  }

  // Check for generic "massage" - default to Swedish
  if (/\bmassage\b/i.test(normalizedInput) && !/deep|tissue|sports/i.test(normalizedInput)) {
    return spaTreatments.find(t => t.id === 'swedish-massage') || null;
  }

  return null;
}

/**
 * Parse date/time from voice input
 * Returns { date: string (YYYY-MM-DD), time: string (HH:MM) } or partial
 */
export function parseDateTimeFromVoice(input: string): { date?: string; time?: string } {
  const result: { date?: string; time?: string } = {};
  const normalizedInput = input.toLowerCase().trim();
  const now = new Date();

  // Parse date
  if (/\btoday\b/i.test(normalizedInput)) {
    result.date = formatDate(now);
  } else if (/\btomorrow\b/i.test(normalizedInput)) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    result.date = formatDate(tomorrow);
  } else if (/\bday after tomorrow\b/i.test(normalizedInput)) {
    const dayAfter = new Date(now);
    dayAfter.setDate(dayAfter.getDate() + 2);
    result.date = formatDate(dayAfter);
  } else {
    // Ordinal date: "24th", "1st", "23rd" — takes priority over weekday name
    const ordinalMatch = normalizedInput.match(/\b(\d{1,2})(?:st|nd|rd|th)\b/i);
    if (ordinalMatch) {
      const targetDay = parseInt(ordinalMatch[1]!);
      const todayStr = formatDate(now);
      // Try current month first
      const candidate = new Date(now.getFullYear(), now.getMonth(), targetDay);
      if (formatDate(candidate) < todayStr) {
        // Past this month — advance to next month
        candidate.setMonth(candidate.getMonth() + 1);
      }
      result.date = formatDate(candidate);
    } else {
      // Weekday names: "tuesday", "next friday", etc.
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      for (let i = 0; i < dayNames.length; i++) {
        if (new RegExp(`\\b${dayNames[i]}\\b`, 'i').test(normalizedInput)) {
          let daysUntil = i - now.getDay();
          if (daysUntil <= 0) daysUntil += 7; // always book in the future
          if (/\bnext\b/i.test(normalizedInput)) daysUntil += 7;
          const d = new Date(now);
          d.setDate(d.getDate() + daysUntil);
          result.date = formatDate(d);
          break;
        }
      }
    }
  }

  // Parse time - various formats
  // Note: Use (?=\s|$|[^a-zA-Z0-9]) instead of \b at end because \b doesn't work after "." in "a.m."
  const timePatterns: Array<{ pattern: RegExp; parse: (m: RegExpMatchArray) => string }> = [
    // "4:30 PM", "10:00 a.m." - check these FIRST (more specific)
    { pattern: /\b(\d{1,2}):(\d{2})\s*(pm|p\.?m\.?)(?=\s|$|[^a-zA-Z0-9])/i, parse: (m) => {
      const h = parseInt(m[1]!);
      return `${h < 12 ? h + 12 : h}:${m[2]}`;
    }},
    { pattern: /\b(\d{1,2}):(\d{2})\s*(am|a\.?m\.?)(?=\s|$|[^a-zA-Z0-9])/i, parse: (m) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
    // "4 PM", "4PM", "4 p.m."
    { pattern: /\b(\d{1,2})\s*(pm|p\.?m\.?)(?=\s|$|[^a-zA-Z0-9])/i, parse: (m) => {
      const h = parseInt(m[1]!);
      return `${h < 12 ? h + 12 : h}:00`;
    }},
    // "4 AM", "4AM", "4 a.m."
    { pattern: /\b(\d{1,2})\s*(am|a\.?m\.?)(?=\s|$|[^a-zA-Z0-9])/i, parse: (m) => `${m[1]!.padStart(2, '0')}:00` },
    // "16:00", "14:30" (24-hour format)
    { pattern: /\b(\d{1,2}):(\d{2})\b/i, parse: (m) => `${m[1]!.padStart(2, '0')}:${m[2]}` },
    // Word times - afternoon
    { pattern: /\b(one|1)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|pm))?\b/i, parse: () => '13:00' },
    { pattern: /\b(two|2)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|pm))?\b/i, parse: () => '14:00' },
    { pattern: /\b(three|3)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|pm))?\b/i, parse: () => '15:00' },
    { pattern: /\b(four|4)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|pm))?\b/i, parse: () => '16:00' },
    { pattern: /\b(five|5)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|pm))?\b/i, parse: () => '17:00' },
    { pattern: /\b(six|6)\s*(o.?clock)?(\s*(in the\s*)?(afternoon|evening|pm))?\b/i, parse: () => '18:00' },
    // Morning times
    { pattern: /\b(nine|9)\s*(o.?clock)?(\s*(in the\s*)?(morning|am))?\b/i, parse: () => '09:00' },
    { pattern: /\b(ten|10)\s*(o.?clock)?(\s*(in the\s*)?(morning|am))?\b/i, parse: () => '10:00' },
    { pattern: /\b(eleven|11)\s*(o.?clock)?(\s*(in the\s*)?(morning|am))?\b/i, parse: () => '11:00' },
    { pattern: /\btwelve|12|noon\b/i, parse: () => '12:00' },
  ];

  for (const { pattern, parse } of timePatterns) {
    const match = normalizedInput.match(pattern);
    if (match) {
      result.time = parse(match);
      break;
    }
  }

  return result;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

/**
 * Format duration for display
 */
export function formatDuration(minutes: number): string {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }
  return `${minutes} min`;
}

/**
 * Format time for speech
 */
export function formatTimeForSpeech(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const h = hours! % 12 || 12;
  const ampm = hours! >= 12 ? 'PM' : 'AM';
  return minutes ? `${h}:${minutes.toString().padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
}

/**
 * Format date for speech
 */
export function formatDateForSpeech(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.getTime() === today.getTime()) {
    return 'today';
  } else if (date.getTime() === tomorrow.getTime()) {
    return 'tomorrow';
  } else {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }
}
